import React, { useState } from 'react';
import { Calendar, User, ArrowRight, Search, Heart, Eye, MessageCircle } from 'lucide-react';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [likedPosts, setLikedPosts] = useState({});

  const blogPosts = [
    {
      id: 1,
      title: 'Top 10 Event Trends for 2025',
      excerpt: 'Discover the latest trends shaping the event management industry in 2025, from hybrid events to sustainable practices.',
      author: 'Rashmi Sharma',
      date: 'Dec 5, 2024',
      category: 'Trends',
      image: '/blog/blog1.jpeg',
      readTime: '5 min read',
      views: 1245,
      comments: 23,
      likes: 89
    },
    {
      id: 2,
      title: 'How to Plan a Successful Corporate Event',
      excerpt: 'A comprehensive guide to organizing memorable corporate events that engage employees and leave lasting impressions.',
      author: 'Prawal Kumar',
      date: 'Nov 28, 2024',
      category: 'Corporate',
      image: '/blog/blog2.jpeg',
      readTime: '8 min read',
      views: 2130,
      comments: 45,
      likes: 156
    },
    {
      id: 3,
      title: 'Wedding Planning 101: Essential Checklist',
      excerpt: 'Everything you need to know about planning your dream wedding, from venue selection to final touches.',
      author: 'Ananya Reddy',
      date: 'Nov 20, 2024',
      category: 'Weddings',
      image: '/blog/blog3.jpeg',
      readTime: '10 min read',
      views: 3420,
      comments: 78,
      likes: 234
    },
    {
      id: 4,
      title: 'The Rise of Virtual and Hybrid Events',
      excerpt: 'Explore how virtual and hybrid events are transforming the industry and creating new opportunities for engagement.',
      author: 'Manoj Singh',
      date: 'Nov 15, 2024',
      category: 'Technology',
      image: '/blog/blog4.jpeg',
      readTime: '6 min read',
      views: 1890,
      comments: 34,
      likes: 112
    },
    {
      id: 5,
      title: 'Budget-Friendly Event Decoration Ideas',
      excerpt: 'Creative and cost-effective decoration ideas that will make your event stunning without breaking the bank.',
      author: 'Sonia Malhotra',
      date: 'Nov 10, 2024',
      category: 'Decoration',
      image: '/blog/blog5.jpeg',
      readTime: '7 min read',
      views: 2567,
      comments: 56,
      likes: 178
    },
    {
      id: 6,
      title: 'Corporate Team Building Activities That Work',
      excerpt: 'Proven team building activities that boost morale, improve communication, and strengthen workplace relationships.',
      author: 'Vikram Patel',
      date: 'Nov 5, 2024',
      category: 'Corporate',
      image: '/blog/blog6.jpeg',
      readTime: '6 min read',
      views: 1678,
      comments: 29,
      likes: 95
    }
  ];

  const categories = ['All', 'Trends', 'Corporate', 'Weddings', 'Technology', 'Decoration'];
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleLike = (postId, event) => {
    event.stopPropagation();
    setLikedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const getLikeCount = (postId, baseLikes) => {
    return likedPosts[postId] ? baseLikes + 1 : baseLikes;
  };

  return (
    <section id="blog" className="py-20 md:py-28 bg-gradient-to-br from-orange-50/30 via-white to-[#b8d4d4]/20 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-orange-200/30 to-transparent rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-[#b8d4d4]/30 to-transparent rounded-full blur-3xl -z-10"></div>

      <div className="container mx-auto px-6">
        {/* Header Section */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <p className="text-orange-500 uppercase tracking-widest text-sm font-bold mb-3" style={{ fontFamily: "'Shadows Into Light', cursive" }}>
            Our Blog
          </p>
          <h2 className="text-6xl md:text-7xl font-black text-gray-900 mb-6 leading-tight" style={{ fontFamily: "'Diplomata', cursive", letterSpacing: '-0.02em' }}>
            Latest Stories
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed" style={{ fontFamily: "'Delius', cursive" }}>
            Insights, tips, and inspiration for creating unforgettable events
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-10">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search for articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-6 py-4 rounded-2xl border border-gray-200 focus:border-orange-400 focus:outline-none focus:ring-4 focus:ring-orange-100 transition-all shadow-sm"
              style={{ fontFamily: "'Delius', cursive" }}
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-7 py-3 rounded-full font-bold text-sm transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-200 scale-105'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-orange-400 hover:shadow-md'
              }`}
              style={{ fontFamily: "'Shadows Into Light', cursive" }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-7xl mx-auto">
          {filteredPosts.map((post) => (
            <article
              key={post.id}
              className="group bg-white rounded-3xl shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                {/* Category Badge */}
                <div className="absolute top-5 left-5 bg-white/95 backdrop-blur-sm text-orange-600 px-5 py-2 rounded-full text-xs font-bold shadow-lg" style={{ fontFamily: "'Shadows Into Light', cursive" }}>
                  {post.category}
                </div>
                {/* Like Button on Image */}
                <button
                  onClick={(e) => toggleLike(post.id, e)}
                  className="absolute top-5 right-5 p-3 bg-white/95 backdrop-blur-sm rounded-full shadow-lg hover:scale-110 transition-transform"
                >
                  <Heart
                    className={`w-5 h-5 transition-all ${
                      likedPosts[post.id]
                        ? 'fill-red-500 text-red-500'
                        : 'text-gray-600'
                    }`}
                  />
                </button>
              </div>

              {/* Content */}
              <div className="p-7">
                {/* Meta Info */}
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    <span style={{ fontFamily: "'Delius', cursive" }}>{post.date}</span>
                  </div>
                  <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                  <div className="flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    <span style={{ fontFamily: "'Delius', cursive" }}>{post.author}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 
                  className="text-2xl font-black text-gray-900 mb-3 group-hover:text-orange-600 transition-colors line-clamp-2 leading-tight" 
                  style={{ fontFamily: "'Arbutus', cursive" }}
                >
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p 
                  className="text-gray-600 mb-5 line-clamp-3 leading-relaxed" 
                  style={{ fontFamily: "'Delius', cursive" }}
                >
                  {post.excerpt}
                </p>

                {/* Stats Row */}
                <div className="flex items-center justify-between pb-5 mb-5 border-b border-gray-100">
                  <div className="flex items-center gap-5 text-sm text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <Eye className="w-4 h-4" />
                      <span style={{ fontFamily: "'Delius', cursive" }}>{post.views}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MessageCircle className="w-4 h-4" />
                      <span style={{ fontFamily: "'Delius', cursive" }}>{post.comments}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Heart className={`w-4 h-4 ${likedPosts[post.id] ? 'fill-red-500 text-red-500' : ''}`} />
                      <span style={{ fontFamily: "'Delius', cursive" }}>{getLikeCount(post.id, post.likes)}</span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400" style={{ fontFamily: "'Delius', cursive" }}>
                    {post.readTime}
                  </span>
                </div>

                {/* Read More Button */}
                <button className="flex items-center gap-2 text-orange-600 font-bold hover:gap-4 transition-all duration-300 group/btn">
                  <span style={{ fontFamily: "'Shadows Into Light', cursive", fontSize: '16px' }}>Read Article</span>
                  <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* No Results Message */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-2xl text-gray-500" style={{ fontFamily: "'Delius', cursive" }}>
              No articles found. Try a different search or category.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Blog;